import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import Icon from '../app_components/Icon';
import ProFeaturesList from './ProFeaturesList';
import { primitives } from '@lexody/design-tokens';

const MONTHLY_PLAN_IDS = [1, 3, 5, 7, 9];
const YEARLY_PLAN_IDS = [2, 4, 6, 8, 10];

class MembershipChoices extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      monthly: false,
      yearly: false,
      subscription: null,
    };

    this.setSubscription = this.setSubscription.bind(this);
    this.onSelectMembership = this.onSelectMembership.bind(this);
    this.membershipStatus = this.membershipStatus.bind(this);
    this.nextRenewalDate = this.nextRenewalDate.bind(this);
  }

  setSubscription(subscription) {
    const planId = subscription != null ? parseInt(subscription.plan_id, 10) : null;
    if (subscription != null && MONTHLY_PLAN_IDS.includes(planId)) {
      this.setState({ monthly: true, yearly: false, subscription });
    } else if (subscription != null && YEARLY_PLAN_IDS.includes(planId)) {
      this.setState({ monthly: false, yearly: true, subscription });
    } else {
      this.setState({ monthly: false, yearly: false, subscription });
    }
  }

  componentDidMount() {
    this.setSubscription(this.props.subscription);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.subscription !== this.props.subscription) {
      this.setSubscription(this.props.subscription);
    }
  }

  onSelectMembership(planId) {
    if (this.props.current_user === null) {
      window.location.assign('/sign_in');
      return;
    }
    this.props.openCheckout(planId);
  }

  hasActiveMembership() {
    const { subscription, current_user, currentMembership } = this.props;
    if (currentMembership) {
      return true;
    }
    if (subscription != null && subscription.plan_id != null) {
      return true;
    }
    if (current_user && (current_user.premium || current_user.basic_forever)) {
      return true;
    }
    return false;
  }

  membershipTypeLabel() {
    const { subscription, currentMembership, freeMembership } = this.props;

    if (freeMembership) {
      return 'Pro';
    }

    if (subscription != null && subscription.plan_id != null) {
      const planId = parseInt(subscription.plan_id, 10);
      if (MONTHLY_PLAN_IDS.includes(planId)) {
        return 'Pro Monthly';
      }
      if (YEARLY_PLAN_IDS.includes(planId)) {
        return 'Pro Yearly';
      }
    }

    if (currentMembership) {
      return currentMembership;
    }

    return 'Pro';
  }

  nextRenewalDate(subscription) {
    if (!subscription || subscription.plan_id == null) {
      return null;
    }

    const planId = parseInt(subscription.plan_id, 10);

    if (MONTHLY_PLAN_IDS.includes(planId)) {
      const renewalDay = moment(subscription.updated_at).date();
      const today = moment().date();
      if (today <= renewalDay) {
        return moment().date(renewalDay);
      }
      return moment().add(1, 'month').date(renewalDay);
    }

    if (YEARLY_PLAN_IDS.includes(planId)) {
      const anchor = moment(subscription.updated_at);
      const thisYearRenewal = anchor.clone().year(moment().year());
      if (moment().isBefore(thisYearRenewal, 'day')) {
        return thisYearRenewal;
      }
      return anchor.clone().year(moment().year() + 1);
    }

    return null;
  }

  membershipStatus() {
    if (!this.hasActiveMembership()) {
      return null;
    }

    const { subscription, freeMembership } = this.props;
    const membershipType = this.membershipTypeLabel();

    if (subscription != null && subscription.cancel_date != null) {
      return {
        membershipType,
        action: 'cancels',
        date: moment(subscription.cancel_date),
      };
    }

    if (freeMembership && (subscription == null || subscription.plan_id == null)) {
      return {
        membershipType,
        action: null,
        date: null,
      };
    }

    const renewalDate = this.nextRenewalDate(subscription);
    if (renewalDate) {
      return {
        membershipType,
        action: 'renews',
        date: renewalDate,
      };
    }

    return {
      membershipType,
      action: null,
      date: null,
    };
  }

  renderStatusBanner() {
    const status = this.membershipStatus();
    if (!status) {
      return null;
    }

    const { membershipType, action, date } = status;

    return (
      <div className="pricing-status pricing-status--premium">
        <div className="pricing-status__icon">
          <Icon name="calendar" size={18} color={primitives.violet['700']} />
        </div>
        <div className="pricing-status__copy">
          {action && date ? (
            <>
              <p className="pricing-status__label">
                Your {membershipType} membership {action} on
              </p>
              <p className="pricing-status__date">
                {date.format('MMMM D, YYYY').toUpperCase()}
              </p>
            </>
          ) : (
            <>
              <p className="pricing-status__label">Your current membership</p>
              <p className="pricing-status__date">{membershipType.toUpperCase()}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  renderPlanCard(variant, planId, selected, badge, badgeClass, icon, features, btnClass, btnLabel) {
    const isYearly = variant === 'yearly';
    const actionLabel = selected ? 'Current plan' : btnLabel;

    return (
      <button
        type="button"
        className={`pricing-plan pricing-plan--${variant}${selected ? ' pricing-plan--selected' : ''}`}
        onClick={() => this.onSelectMembership(planId)}
        disabled={selected}
        aria-label={selected ? `Your current ${isYearly ? 'yearly' : 'monthly'} plan` : `${btnLabel} — ${isYearly ? '$39 per year' : '$7 per month'}`}
      >
        <span className={`pricing-plan__badge pricing-plan__badge--${badgeClass}`}>{badge}</span>

        <div className="pricing-plan__icon">
          <Icon
            name={icon}
            size={28}
            color={isYearly ? '#fff' : primitives.pink['500']}
          />
        </div>

        <p className="pricing-plan__price">{isYearly ? '$39' : '$7'}</p>
        <p className="pricing-plan__interval">{isYearly ? 'per year' : 'per month'}</p>

        {selected ? (
          <span className="pricing-plan__active-label">Your current plan</span>
        ) : isYearly ? (
          <span className="pricing-plan__save">Save 54%</span>
        ) : null}

        <ul className="pricing-plan__features">
          {features.map((feature) => (
            <li key={feature} className="pricing-plan__feature">
              <span className="pricing-plan__check-circle" aria-hidden="true">
                <Icon name="check" size={14} color={isYearly ? '#fff' : primitives.pink['500']} />
              </span>
              {feature}
            </li>
          ))}
        </ul>

        <span className={`pricing-plan__btn pricing-plan__btn--${btnClass}`} aria-hidden="true">
          {actionLabel}
        </span>
      </button>
    );
  }

  renderSocialProof() {
    const { images } = this.props;
    if (!images || !images.user) {
      return (
        <div className="pricing-social">
          <p className="pricing-social__text">
            Join thousands of language learners supporting Lexody ❤️
          </p>
        </div>
      );
    }

    return (
      <div className="pricing-social">
        <div className="pricing-social__avatars" aria-hidden="true">
          <img src={images.user} alt="" />
          <img src={images.user} alt="" />
          <img src={images.user} alt="" />
        </div>
        <p className="pricing-social__text">
          Join thousands of language learners supporting Lexody ❤️
        </p>
      </div>
    );
  }

  render() {
    const monthlyFeatures = ['Cancel anytime', 'Billed monthly', 'Full access to Pro features'];
    const yearlyFeatures = ['Billed yearly', 'Full access to Pro features'];

    return (
      <div>
        {this.renderStatusBanner()}

        <div className="pricing-plans">
          {this.renderPlanCard(
            'monthly',
            5,
            this.state.monthly,
            'Most Popular',
            'popular',
            'award',
            monthlyFeatures,
            'pink',
            'Choose Monthly'
          )}
          {this.renderPlanCard(
            'yearly',
            6,
            this.state.yearly,
            'Best Value',
            'value',
            'star',
            yearlyFeatures,
            'white',
            'Choose Yearly'
          )}
        </div>

        <ProFeaturesList />
        {this.renderSocialProof()}
      </div>
    );
  }
}

MembershipChoices.propTypes = {
  subscription: PropTypes.object,
  current_user: PropTypes.object,
  currentMembership: PropTypes.string,
  freeMembership: PropTypes.bool,
  openCheckout: PropTypes.func.isRequired,
  images: PropTypes.object,
};

export default MembershipChoices;
